import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { STATUS_LABEL, formatarDinheiro } from '@/pages/gerenciar/utils';
import type { StatusInscricao } from '@/components/feature/SeloSituacao';

type Estado = 'carregando' | 'pronto' | 'sem-acesso' | 'erro';

interface LinhaInscrito {
  inscricao_id: number;
  modalidade_id: number;
  modalidade: string;
  grupo: string | null;
  participante: string;
  participante_telefone: string | null;
  status: StatusInscricao;
}

interface LinhaFila {
  inscricao_id: number;
  posicao: number;
  senha: string | null;
}

interface LinhaSaldo {
  inscricao_id: number;
  saldo_centavos: number;
}

interface LinhaResposta {
  inscricao_id: number;
  enunciado: string;
  resposta_texto: string | null;
}

interface GrupoVagas {
  vagas: number | null;
}

interface LinhaLista {
  inscricaoId: number;
  nome: string;
  modalidade: string;
  grupo: string | null;
  situacao: string;
  senha: string | null;
  telefone: string | null;
  cidade: string | null;
  pago: string;
}

function normalizar(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

export default function ListaImprimirPage() {
  const navigate = useNavigate();
  const { user, pronto: authPronto } = useAuth();
  const { eventoId } = useParams();
  const [searchParams] = useSearchParams();
  const eventoIdNum = Number(eventoId);

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [eventoNome, setEventoNome] = useState('');
  const [linhas, setLinhas] = useState<LinhaLista[]>([]);

  const titulo = searchParams.get('titulo') ?? 'Todos os inscritos';
  const ids = useMemo(
    () =>
      (searchParams.get('ids') ?? '')
        .split(',')
        .map((v) => Number(v.trim()))
        .filter((v) => Number.isFinite(v) && v > 0),
    [searchParams],
  );

  useEffect(() => {
    if (!authPronto) return;
    if (!user) {
      navigate(`/entrar?voltar=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (Number.isNaN(eventoIdNum) || ids.length === 0) {
      setEstado('sem-acesso');
      return;
    }
    let ativo = true;
    (async () => {
      setEstado('carregando');
      setErro(null);
      try {
        const { data: ev, error: errEv } = await supabase
          .from('eventos')
          .select('id, nome, dono_id')
          .eq('id', eventoIdNum)
          .maybeSingle();
        if (errEv) throw errEv;
        if (!ev) {
          if (ativo) setEstado('sem-acesso');
          return;
        }
        const evento = ev as { nome: string; dono_id: string };
        let permitido = evento.dono_id === user.id;
        if (!permitido) {
          const { data: equipe } = await supabase
            .from('equipe_evento')
            .select('id')
            .eq('evento_id', eventoIdNum)
            .eq('usuario_id', user.id)
            .eq('status', 'ativo')
            .maybeSingle();
          permitido = !!equipe;
        }
        if (!permitido) {
          if (ativo) setEstado('sem-acesso');
          return;
        }

        const [resInscritos, resFila, resSaldo, resPainel, resRespostas] = await Promise.all([
          supabase
            .from('vw_inscritos')
            .select(
              'inscricao_id, modalidade_id, modalidade, grupo, participante, participante_telefone, status',
            )
            .eq('evento_id', eventoIdNum)
            .in('inscricao_id', ids),
          supabase.rpc('fila_de_suplentes', { p_evento: eventoIdNum }),
          supabase
            .from('vw_saldo_inscricao')
            .select('inscricao_id, saldo_centavos')
            .eq('evento_id', eventoIdNum),
          supabase.rpc('painel_vagas', { p_evento: eventoIdNum }),
          supabase
            .from('vw_respostas_inscricao')
            .select('inscricao_id, enunciado, resposta_texto')
            .in('inscricao_id', ids),
        ]);
        if (resInscritos.error) throw resInscritos.error;

        const posicaoMap = new Map<number, number>();
        const senhaMap = new Map<number, string | null>();
        ((resFila.data as LinhaFila[]) || []).forEach((f) => {
          posicaoMap.set(Number(f.inscricao_id), f.posicao);
          senhaMap.set(Number(f.inscricao_id), f.senha);
        });

        const saldoMap = new Map<number, number>();
        ((resSaldo.data as LinhaSaldo[]) || []).forEach((s) =>
          saldoMap.set(Number(s.inscricao_id), Number(s.saldo_centavos)),
        );

        const temVagas = ((resPainel.data as GrupoVagas[]) || []).some(
          (g) => g.vagas !== null && g.vagas !== undefined,
        );

        const cidadeMap = new Map<number, string>();
        ((resRespostas.data as LinhaResposta[]) || []).forEach((r) => {
          if (!r.resposta_texto) return;
          if (!normalizar(r.enunciado).includes('cidade')) return;
          if (!cidadeMap.has(r.inscricao_id)) cidadeMap.set(r.inscricao_id, r.resposta_texto);
        });

        const ordem = new Map<number, number>();
        ids.forEach((id, i) => ordem.set(id, i));

        const lista: LinhaLista[] = ((resInscritos.data as LinhaInscrito[]) || [])
          .slice()
          .sort((a, b) => (ordem.get(Number(a.inscricao_id)) ?? 0) - (ordem.get(Number(b.inscricao_id)) ?? 0))
          .map((i) => {
            const id = Number(i.inscricao_id);
            const saldo = saldoMap.get(id);
            const posicao = posicaoMap.get(id) ?? null;
            let situacao: string;
            if (i.status === 'cancelada') situacao = 'Cancelada';
            else if (i.status === 'lista_espera') {
              situacao = posicao ? `Suplente nº ${posicao}` : 'Suplente';
            } else if (temVagas) situacao = 'Com vaga';
            else situacao = STATUS_LABEL[i.status] ?? '—';

            let pago = '—';
            if (i.status === 'cancelada' || i.status === 'lista_espera') pago = '—';
            else if (saldo === undefined) pago = '—';
            else if (saldo <= 0) pago = 'Pago';
            else pago = `Falta ${formatarDinheiro(saldo)}`;

            return {
              inscricaoId: id,
              nome: i.participante,
              modalidade: i.modalidade,
              grupo: i.grupo,
              situacao,
              senha: senhaMap.get(id) ?? null,
              telefone: i.participante_telefone,
              cidade: cidadeMap.get(id) ?? null,
              pago,
            };
          });

        if (!ativo) return;
        setEventoNome(evento.nome);
        setLinhas(lista);
        setEstado('pronto');
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e));
          setEstado('erro');
        }
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authPronto, user, eventoIdNum, ids.join(',')]);

  if (estado === 'carregando') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100">
        <span className="flex h-12 w-12 items-center justify-center text-primary-500">
          <i className="ri-loader-4-line animate-spin text-4xl" />
        </span>
      </div>
    );
  }

  if (estado === 'erro' || estado === 'sem-acesso') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] rounded-2xl border border-secondary-200 bg-white p-8 text-center">
          <h1 className="text-xl font-medium text-foreground-950">
            {estado === 'erro' ? 'Não foi possível carregar' : 'Sem acesso'}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {erro ?? 'Você não administra este evento.'}
          </p>
          <button
            type="button"
            onClick={() => navigate(`/gerenciar/${eventoId}`)}
            className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Voltar ao painel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          body { background: #fff !important; }
          .nao-imprimir { display: none !important; }
          .folha-lista { border: 0 !important; border-radius: 0 !important; padding: 0 !important; }
        }
      `}</style>

      <div className="nao-imprimir mx-auto w-full max-w-[1100px] px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-900"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-left-line" />
          </span>
          Voltar
        </button>
        <h1 className="mt-2 text-2xl font-bold text-foreground-950">Imprimir lista</h1>
        <p className="text-sm text-secondary-500">
          {eventoNome} · {titulo} · {linhas.length} {linhas.length === 1 ? 'pessoa' : 'pessoas'}
        </p>
        <button
          type="button"
          onClick={() => window.print()}
          className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-printer-line" />
          </span>
          Imprimir
        </button>
      </div>

      <div className="folha-lista mx-auto w-full max-w-[1100px] rounded-lg border border-secondary-200 bg-white p-6">
        <header className="border-b border-secondary-200 pb-2">
          <p className="text-[10pt] font-semibold uppercase tracking-wide text-primary-700">
            {eventoNome}
          </p>
          <h1 className="text-[15pt] font-bold text-foreground-950">{titulo}</h1>
          <p className="text-[9pt] text-secondary-600">
            {linhas.length} {linhas.length === 1 ? 'pessoa' : 'pessoas'}
          </p>
        </header>

        <table className="mt-3 w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-secondary-300 text-[8.5pt] uppercase tracking-wide text-secondary-500">
              <th className="py-1.5 pr-2 font-semibold">Nome</th>
              <th className="py-1.5 pr-2 font-semibold">Participação</th>
              <th className="py-1.5 pr-2 font-semibold">Grupo</th>
              <th className="py-1.5 pr-2 font-semibold">Situação</th>
              <th className="py-1.5 pr-2 font-semibold">Senha</th>
              <th className="py-1.5 pr-2 font-semibold">Telefone</th>
              <th className="py-1.5 pr-2 font-semibold">Cidade</th>
              <th className="py-1.5 font-semibold">Pago/Falta</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.inscricaoId} className="border-b border-secondary-100 text-[9.5pt]">
                <td className="py-1.5 pr-2 font-medium text-foreground-950">{l.nome}</td>
                <td className="py-1.5 pr-2 text-foreground-800">{l.modalidade}</td>
                <td className="py-1.5 pr-2 text-foreground-800">{l.grupo || '—'}</td>
                <td className="py-1.5 pr-2 text-foreground-800">{l.situacao}</td>
                <td className="py-1.5 pr-2 text-foreground-800">{l.senha || '—'}</td>
                <td className="py-1.5 pr-2 text-foreground-800">{l.telefone || '—'}</td>
                <td className="py-1.5 pr-2 text-foreground-800">{l.cidade || '—'}</td>
                <td className="py-1.5 text-foreground-800">{l.pago}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}